<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import api from '@/api';
import { MAX_BOX_FOLDER_URLS } from '@/constants';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import { BoxFolder, UserDocumentStorageStatus } from '@/types';

const { createSnackbar } = useSnackbar();

const boxFolders = ref<BoxFolder[]>([]);
const storageStatus = ref<UserDocumentStorageStatus>();

// storageStatusが取得できない場合、または同期中は編集ページに遷移させない。
const isEditButtonDisabled = computed(
  () => !storageStatus.value || storageStatus.value?.status === 'syncing',
);

onMounted(async () => {
  try {
    boxFolders.value = await api.getBoxFolders(true);
  } catch (error) {
    createSnackbar({
      message: 'BOXフォルダの取得に失敗しました',
      type: 'error',
    });
    // eslint-disable-next-line no-console
    console.error(error);

    // フォルダ情報の取得に失敗した場合は、storageStatusを取得しない
    return;
  }
  storageStatus.value = await api.getTeamStorageStatus('box');
});
</script>

<template>
  <div class="box-folders">
    <Header title="BOXフォルダ情報" header-width="100%" is-one-line />
    <div class="content">
      <div class="box-folder-list-content">
        <div class="content-header">
          <div class="title">
            <div class="c-title-bold--m">BOXフォルダ</div>
            <div class="c-text-regular--s folder-url-count">
              ・{{ boxFolders.length }}件/{{ MAX_BOX_FOLDER_URLS }}件
            </div>
          </div>
          <component
            :is="isEditButtonDisabled ? 'button' : 'router-link'"
            :to="{ name: 'boxFolderEdit' }"
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{ disabled: isEditButtonDisabled }"
            :disabled="isEditButtonDisabled"
          >
            編集
          </component>
        </div>
        <div class="folder-url-list">
          <div class="title c-text-regular--s">URL</div>
          <div
            v-for="(boxFolder, i) in boxFolders"
            :key="i"
            class="folder-url c-text-regular--m"
          >
            {{ boxFolder.folder_url }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.box-folders {
  width: 100%;
  padding: 0 !important;
}
.box-folder-list-content {
  padding: 24px;
}
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .title {
    display: flex;
    align-items: center;
    gap: 4px;
    .folder-url-count {
      color: $color-gray600;
    }
  }
}
.folder-url-list {
  background-color: $color-white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 16px;
  .title {
    padding: 8px 0;
    border-bottom: 1px solid $color-gray400;
  }
}
.folder-url {
  padding: 8px;
  min-height: 32px;
  border-bottom: 1px solid $color-gray400;
  display: flex;
  align-items: center;
  word-break: break-all;
  &:hover:not(:has(.action-icon:hover)) {
    background: $color-gray200;
  }
}
</style>
